import React, { useState, useEffect } from 'react';
import { 
  User, Mail, AlertCircle, CreditCard, X, Coins, ChevronDown, ChevronUp,
  MoreVertical, Edit, Trash2, DollarSign, UserCheck, UserX
} from 'lucide-react';
import { useTranslation } from '../../../../contexts/LanguageContext';

const MobileMemberList = ({ 
  members, 
  space, 
  currentUserId, 
  isAdmin, 
  onRemoveMember, 
  onCreateTransaction,
  monthlyCost
}) => {
  const { t } = useTranslation();
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [expandedMember, setExpandedMember] = useState(null);

  useEffect(() => {
    console.log('Members updated:', members);
  }, [members, space.admin_id]);

  const calculateNetBalance = (member) => {
    return member.balance || 0;
  };
  
  const calculateMemberShare = (memberId) => {
    const acceptedMembers = members.filter(m => m.request_status === 'accepted');
    const memberCount = acceptedMembers.length;
    return memberCount > 0 ? monthlyCost / memberCount : 0;
  };

  const handleRecordPayment = (member) => {
    setSelectedMember(member);
    setPaymentAmount('');
    setShowPaymentModal(true);
  };

  const submitPayment = () => {
    if (!paymentAmount || isNaN(paymentAmount) || parseFloat(paymentAmount) <= 0) return;
    
    const amount = parseFloat(paymentAmount);
    
    onCreateTransaction(selectedMember.member_id, amount, t('sharesubs.members.paymentReceived'))
      .then(success => {
        if (success) {
          setShowPaymentModal(false);
          setSelectedMember(null);
        }
      });
  };

  const confirmRemoveMember = (member) => {
    setMemberToRemove(member);
  };

  const executeRemoveMember = () => {
    if (memberToRemove) {
      const netBalance = calculateNetBalance(memberToRemove);
      
      if (netBalance < 0) {
        onCreateTransaction(memberToRemove.member_id, Math.abs(netBalance), t('sharesubs.members.balanceSettlement'))
          .then(() => {
            onRemoveMember(space, memberToRemove.member_id);
          });
      } else {
        onRemoveMember(space, memberToRemove.member_id);
      }
      
      setMemberToRemove(null);
    }
  };

  const toggleMemberExpansion = (memberId) => {
    if (expandedMember === memberId) {
      setExpandedMember(null);
    } else {
      setExpandedMember(memberId);
    }
  };

  return (
    <div className="mb-4">
      <h4 className="font-bold text-lg text-gray-800 mb-3 flex items-center">
        <User className="h-5 w-5 mr-2 text-[#253985]" /> 
        {t('sharesubs.members.title')} ({members.filter(m => m.request_status === 'accepted').length})
      </h4>
      
      <div className="border border-gray-200 rounded-lg divide-y">
        {members.map((member, index) => {
          if (member.request_status !== 'accepted') return null;
          
          const netBalance = calculateNetBalance(member);
          const isCurrentUser = member.member_id === currentUserId;
          const displayName = member.username || member.member_email;
          const memberShare = calculateMemberShare(member.member_id);
          const isExpanded = expandedMember === member.member_id;
          
          return (
            <div key={index} className="p-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1 min-w-0">
                  <div className="bg-[#253985] bg-opacity-10 w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <User className="h-4 w-4 text-[#253985]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate flex items-center">
                      {displayName}
                      {member.member_id === space.admin_id && (
                        <span className="ml-2 bg-yellow-100 text-yellow-800 text-xs px-1.5 py-0.5 rounded-full">
                          {t('sharesubs.members.admin')}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate flex items-center mt-0.5">
                      {t('sharesubs.costSummary.perPerson')}: €{memberShare.toFixed(2)}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center ml-2">
                  <div className="text-right mr-2">
                    <div className={`text-sm font-semibold ${
                      netBalance < 0 ? 'text-red-500' : netBalance > 0 ? 'text-green-500' : 'text-gray-500'
                    }`}>
                      {netBalance < 0 ? `-€${Math.abs(netBalance).toFixed(2)}` : `+€${netBalance.toFixed(2)}`}
                    </div>
                  </div>
                  <button 
                    onClick={() => toggleMemberExpansion(member.member_id)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>
              </div>
              
              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  {/* Additional member details */}
                  <div className="mb-3 space-y-2">
                    <div className="flex items-center text-xs text-gray-600">
                      <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{member.member_email}</span>
                    </div>
                    {isCurrentUser && (
                      <div className="flex">
                        <span className="bg-[#253985] text-white text-xs px-1.5 py-0.5 rounded-full">
                          {t('sharesubs.members.you')}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex flex-col gap-2">
                    {isAdmin && member.member_id !== space.admin_id && (
                      <>
                        {netBalance < 0 && (
                          <button 
                            onClick={() => handleRecordPayment(member)}
                            className="w-full py-2 bg-[#253985] text-white text-sm rounded-lg flex items-center justify-center"
                          >
                            <CreditCard className="h-4 w-4 mr-1" />
                            {t('sharesubs.members.recordPayment')}
                          </button>
                        )}
                        <button 
                          onClick={() => confirmRemoveMember(member)}
                          className="w-full py-2 bg-red-600 text-white text-sm rounded-lg flex items-center justify-center"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {t('sharesubs.actions.remove')}
                        </button>
                      </>
                    )}
                    
                    {!isAdmin && isCurrentUser && (
                      <button
                        onClick={() => confirmRemoveMember(member)}
                        className="w-full py-2 border border-red-600 text-red-600 text-sm rounded-lg flex items-center justify-center"
                      >
                        <X className="h-4 w-4 mr-1" />
                        {t('sharesubs.actions.leaveSpace')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Payment Modal */}
      {showPaymentModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {t('sharesubs.members.recordPayment')}
              </h3>
              <button 
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <div className="bg-blue-50 p-3 rounded-lg mb-3 border border-blue-100">
                <div className="flex items-center mb-2">
                  <User className="h-5 w-5 text-[#253985] mr-2" />
                  <span className="font-medium truncate">{selectedMember.name || selectedMember.member_email}</span>
                </div>
                
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600 text-sm">{t('sharesubs.members.currentBalance')}:</span>
                  <span className={`text-base font-semibold ${
                    selectedMember.balance < 0 ? 'text-red-500' : 'text-green-500'
                  }`}>
                    {selectedMember.balance < 0 ? 
                      `-€${Math.abs(selectedMember.balance).toFixed(2)}` : 
                      `+€${selectedMember.balance.toFixed(2)}`
                    }
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {selectedMember.balance < 0 ? 
                    t('sharesubs.members.memberOwes') : 
                    t('sharesubs.members.memberHasCredit')
                  }
                </div>
              </div>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('sharesubs.members.paymentAmount')} (€)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">€</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedMember.balance < 0 ? Math.abs(selectedMember.balance) : undefined}
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full pl-8 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#253985] focus:border-[#253985]"
                  placeholder={t('sharesubs.members.enterAmount')}
                />
              </div>
              
              {selectedMember.balance < 0 && (
                <button
                  onClick={() => setPaymentAmount(Math.abs(selectedMember.balance).toFixed(2))}
                  className="mt-2 w-full py-2 bg-blue-100 text-[#253985] rounded-lg hover:bg-blue-200 flex items-center justify-center transition-colors text-sm"
                >
                  <Coins className="h-4 w-4 mr-1" />
                  {t('sharesubs.members.settleFullBalance')}
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                {t('sharesubs.actions.cancel')}
              </button>
              <button
                onClick={submitPayment}
                disabled={!paymentAmount || isNaN(paymentAmount) || parseFloat(paymentAmount) <= 0 || 
                         (selectedMember.balance < 0 && parseFloat(paymentAmount) > Math.abs(selectedMember.balance))}
                className="flex-1 py-2.5 bg-[#253985] text-white rounded-lg hover:bg-[#1a2c6d] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-sm"
              >
                <CreditCard className="h-4 w-4 mr-1" />
                {t('sharesubs.members.recordPayment')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Remove Member Confirmation Modal */}
      {memberToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                {memberToRemove.member_id === currentUserId ? t('sharesubs.modals.leaveSpace.title') : t('sharesubs.modals.removeMember.title')}
              </h3>
              <button 
                onClick={() => setMemberToRemove(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 text-sm mb-3">
                {memberToRemove.member_id === currentUserId
                  ? t('sharesubs.modals.leaveSpace.description')
                  : t('sharesubs.modals.removeMember.description')
                }
              </p>
              
              {calculateNetBalance(memberToRemove) < 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                  <div className="flex items-center text-yellow-800 mb-1">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    <span className="font-medium">{t('sharesubs.members.outstandingBalance')}</span>
                  </div>
                  <p className="text-yellow-700">
                    {t('sharesubs.members.balanceResolution', {
                      amount: Math.abs(calculateNetBalance(memberToRemove)).toFixed(2),
                      action: memberToRemove.member_id === currentUserId ? t('sharesubs.members.youLeave') : t('sharesubs.members.theyAreRemoved')
                    })}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setMemberToRemove(null)}
                className="flex-1 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                {t('sharesubs.actions.cancel')}
              </button>
              <button
                onClick={executeRemoveMember}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                {memberToRemove.member_id === currentUserId ? t('sharesubs.modals.leaveSpace.leave') : t('sharesubs.modals.removeMember.remove')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileMemberList;